import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";

interface FormData {
  name: string;
  email: string;
  company: string;
  projectType: string;
}

interface StepAboutYouProps {
  form: FormData;
  update: (field: string, value: string) => void;
  emailWarning: boolean;
  setEmailWarning: (v: boolean) => void;
  emailOverridden: boolean;
  setEmailOverridden: (v: boolean) => void;
  isDallasLocal: boolean;
  setIsDallasLocal: (v: boolean) => void;
  dallasOfferType: string;
  setDallasOfferType: (v: string) => void;
}

const dallasOfferOptions = [
  { label: "30% Discount", desc: "Off your final project rate" },
  { label: "$1,000 One-Pager", desc: "Flat-rate business start-up site" },
  { label: "Barter / Trade", desc: "Trade services instead of payment" },
];

const StepAboutYou = ({
  form,
  update,
  emailWarning,
  setEmailWarning,
  emailOverridden,
  setEmailOverridden,
  isDallasLocal,
  setIsDallasLocal,
  dallasOfferType,
  setDallasOfferType,
}: StepAboutYouProps) => {
  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  const [typeOpen, setTypeOpen] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      const { data } = await supabase
        .from("services")
        .select("title")
        .order("display_order", { ascending: true });
      if (data) setProjectTypes(data.map((s) => s.title));
    };
    fetchServices();
  }, []);

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <p className="font-heading text-ts-lg text-foreground mb-1">
          Let's start with the basics
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          Tell me a little about yourself.
        </p>
      </div>

      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-1.5 block">
          Name *
        </label>
        <input
          type="text"
          required
          maxLength={100}
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          className="w-full bg-transparent border-b border-border py-2 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors"
          placeholder="Your name"
        />
      </div>

      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-1.5 block">
          Email *
        </label>
        <input
          type="email"
          required
          maxLength={255}
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className={`w-full bg-transparent border-b py-2 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:outline-none transition-colors ${
            emailWarning ? "border-destructive" : "border-border focus:border-primary"
          }`}
          placeholder="you@company.com"
        />
        {emailWarning && (
          <div className="mt-2 flex items-start gap-2">
            <p className="font-body text-ts-2xs text-destructive flex-1">
              This doesn't look like a valid email. Is it correct?
            </p>
            <button
              type="button"
              onClick={() => {
                setEmailOverridden(true);
                setEmailWarning(false);
              }}
              className="font-body text-ts-2xs font-medium text-foreground underline underline-offset-2 whitespace-nowrap hover:text-primary transition-colors"
            >
              Yes, submit anyway
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-1.5 block">
          Company / Brand
        </label>
        <input
          type="text"
          maxLength={100}
          value={form.company}
          onChange={(e) => update("company", e.target.value)}
          className="w-full bg-transparent border-b border-border py-2 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors"
          placeholder="Optional"
        />
      </div>

      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
          Project type
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setTypeOpen(!typeOpen)}
            className={`w-full text-left bg-transparent border-b py-2 font-body text-ts-xs flex items-center justify-between transition-colors ${
              form.projectType
                ? "text-foreground border-primary"
                : "text-muted-foreground/40 border-border"
            }`}
          >
            <span>{form.projectType || "Select a project type"}</span>
            <svg
              className={`w-2.5 h-2.5 transition-transform ${typeOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 10 6"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m1 1 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {typeOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-background border border-border shadow-lg max-h-[200px] overflow-y-auto">
              {projectTypes.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    update("projectType", form.projectType === opt ? "" : opt);
                    setTypeOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 font-body text-ts-2xs tracking-wide transition-colors ${
                    form.projectType === opt
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dallas Local Toggle */}
      <div className="border-t border-border pt-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body text-ts-xs font-medium text-foreground">
              Dallas Local
            </p>
            <p className="font-body text-ts-2xs text-muted-foreground mt-0.5">
              Unlock our locals-only offers
            </p>
          </div>
          <Switch
            checked={isDallasLocal}
            onCheckedChange={(checked) => {
              setIsDallasLocal(checked);
              if (!checked) setDallasOfferType("");
            }}
          />
        </div>

        <AnimatePresence>
          {isDallasLocal && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden"
            >
              <div className="pt-4 space-y-3">
                <p className="font-body text-ts-2xs text-muted-foreground">
                  Choose your offer:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {dallasOfferOptions.map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() =>
                        setDallasOfferType(dallasOfferType === opt.label ? "" : opt.label)
                      }
                      className={`px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-colors ${
                        dallasOfferType === opt.label
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {dallasOfferType && (
                  <p className="font-body text-ts-2xs text-foreground/50 italic">
                    {dallasOfferOptions.find((o) => o.label === dallasOfferType)?.desc}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StepAboutYou;
